import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApplicationModal from '../../components/admin/ApplicationViewModal';
import { Search, Eye, Download, Filter, FileText, MessageSquare, Trash2 } from 'lucide-react';
import { apiService } from '../../services/api';

export default function AdminApplications() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<any[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getLeaseApplications({ page: 1, limit: 100 });
      
      if (response.status === 'success' && response.data?.applications) {
        console.log('Loaded lease applications:', response.data.applications);
        setApplications(response.data.applications);
        setFilteredApplications(response.data.applications);
      } else {
        setError('Failed to load lease applications');
      }
    } catch (err) {
      console.error('Error loading lease applications:', err);
      setError('Failed to load lease applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = applications.filter(app => {
      const matchesSearch = app.Consent?.ApplicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           app.Company?.CompanyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           app.Company?.EmailAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           app.Company?.PhoneNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || app.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    setFilteredApplications(filtered);
  }, [applications, searchTerm, statusFilter]);

  const handleViewApplication = (application: any) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const handleStartConversation = async (application: any) => {
    try {
      // Start a conversation with this application (without initial message)
      const response = await apiService.startConversation(application._id, '');
      
      if (response.status === 'success') {
        // Navigate to messages page using React Router
        navigate('/admin/messages');
      } else {
        setError('Failed to start conversation');
      }
    } catch (err) {
      setError('Failed to start conversation');
    }
  };

  const handleUpdateStatus = async (applicationId: string, newStatus: string, notes?: string) => {
    console.log('Updating lease application status:', { applicationId, newStatus, notes });
    
    if (!applicationId || applicationId === 'undefined') {
      alert('Invalid application ID');
      return;
    }
    
    try {
      const response = await apiService.updateLeaseApplicationStatus(applicationId, newStatus, notes);
      if (response.status === 'success') {
        // Reload applications to get updated data
        await loadApplications();
      } else {
        alert('Failed to update lease application status');
      }
    } catch (error) {
      console.error('Error updating lease application status:', error);
      alert('Failed to update lease application status. Please try again.');
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await apiService.exportLeaseApplications();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease-applications-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting lease applications:', error);
      alert('Failed to export lease applications. Please try again.');
    }
  };

  const handleDeleteApplication = async (applicationId: string, applicantName: string) => {
    const confirmMessage = `Are you sure you want to delete the application for ${applicantName}? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await apiService.deleteLeaseApplication(applicationId);
      if (response.status === 'success') {
        // Reload applications to get updated data
        await loadApplications();
        alert('Application deleted successfully');
      } else {
        alert('Failed to delete application');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600">Loading applications...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-500 mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Applications</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadApplications}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Lease Applications</h1>
            <p className="text-sm sm:text-base text-gray-600">Review and manage tenant lease applications</p>
          </div>
          <button
            onClick={handleExportCSV}
            className="bg-primary text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors btn-hover flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <Download className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 sm:h-5 sm:w-5" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 sm:pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-light-gray">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applicant
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shop
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Business Type
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredApplications.map((app, index) => (
                  <tr key={app.id || app._id || `app-${index}`} className="hover:bg-light-gray transition-colors">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900">{app.Consent?.ApplicantName || 'N/A'}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{app.Company?.CompanyName || 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs sm:text-sm font-medium text-gray-900">{app.shopNumber || 'N/A'}</div>
                        <div className="text-xs sm:text-sm text-gray-500">{app.shopFloor || 'N/A'} • {app.shopSize ? `${app.shopSize} sqm` : 'N/A'}</div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {app.Company?.CompanyName || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                      {app.Company?.BusinessType || 'N/A'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{app.Company?.EmailAddress || 'N/A'}</div>
                      <div className="text-xs sm:text-sm text-gray-500">{app.Company?.PhoneNumber || 'N/A'}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getStatusColor(app.status)}`}>
                        {app.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                      {new Date(app.submittedAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewApplication(app)}
                          className="text-primary hover:text-primary-dark flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">View</span>
                        </button>
                        {app.status !== 'submitted' && (
                          <button
                            onClick={() => handleStartConversation(app)}
                            className="text-green-600 hover:text-green-700 flex items-center gap-1"
                            title="Start conversation"
                          >
                            <MessageSquare className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">Message</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteApplication(app._id, app.Consent?.ApplicantName || 'Unknown')}
                          className="text-red-600 hover:text-red-700 flex items-center gap-1"
                          title="Delete application"
                        >
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Empty State */}
        {filteredApplications.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No lease applications found</h3>
            <p className="text-gray-600">Try adjusting your search criteria.</p>
          </div>
        )}

        {/* Application View Modal */}
        {showModal && selectedApplication && (
          <ApplicationModal
            application={selectedApplication}
            onClose={() => setShowModal(false)}
            onUpdateStatus={handleUpdateStatus}
          />
        )}
      </div>
  );
}